import { productWizardConsole } from '@athena-lib/ProductWizard/util/productWizardLog';
import type { ProductAttrMapInterface } from '../model/attr/ProductAttrMapInterface';
import type { ProductAttrValueMapInterface } from '../model/attr/ProductAttrValueMapInterface';
import type { ProductItem } from '../model/ProductItem';
import type { ProductItemAttributesInterface } from '../model/ProductItemAttributesInterface';
import type { ProductAttrConditionEvaluator } from './ProductAttrConditionEvaluator';

type AttributeType = string | number | boolean;
/**
 * WARNING! This needs to be on pair with ProductAttrComputer.php
 */
export class ProductAttrComputer {
  public attributes: ProductAttrMapInterface = {};
  public useFilters: boolean = true;
  public attributesConstrained: ProductAttrValueMapInterface = {};
  public attributesFiltered: ProductAttrValueMapInterface = {};
  public attributesOutOfStock: ProductAttrValueMapInterface = {};
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  public attributesFiltersMatched: any = {};
  public attributesSuggested: ProductAttrValueMapInterface = {};
  public attributesPreferred: ProductItemAttributesInterface = {};

  constructor(public attrEvaluator: ProductAttrConditionEvaluator) {}

  public reset(attributes: ProductAttrMapInterface): void {
    this.attributes = attributes;
  }

  public evaluate(currentItem: ProductItem): void {
    this.evaluateFilteredValues(currentItem);
    this.evaluateConstrainedValues(currentItem);
    this.evaluateSuggestedValues();
    this.evaluateOutOfStockValues();
  }

  protected evaluateFilteredValues(currentItem: ProductItem): void {
    productWizardConsole.groupCollapsed('evaluateFilteredValues()');
    this.attributesFiltered = {};
    this.attributesFiltersMatched = {};
    for (const attributeName in this.attributes) {
      this.attributesFiltered[attributeName] = this.attrEvaluator.filter(
        currentItem.toTestableOneDimensionalObject(),
        attributeName,
        (attributeName, values, filtersMatched) => {
          if (filtersMatched && filtersMatched.length) {
            this.attributesFiltersMatched[attributeName] = filtersMatched.length;
          } else {
            this.attributesFiltersMatched[attributeName] = 0;
          }
          return values;
        },
      );
    }
    productWizardConsole.groupEnd();
  }

  protected evaluateConstrainedValues(currentItem: ProductItem): void {
    this.attrEvaluator.clearDebug();
    this.attributesConstrained = {};
    for (const attributeName in this.attributes) {
      const attr = this.attributes[attributeName];
      this.attributesConstrained[attributeName] = [];
      for (const attributeValue in attr.valuesAndConstraints) {
        const result = this.attrEvaluator.canIChangeAttribute(
          currentItem.toTestableOneDimensionalObject(),
          attributeName,
          attributeValue,
        );
        if (!result) {
          this.attributesConstrained[attributeName].push(attributeValue);
        }
      }
    }
  }

  /**
   * Suggested values are filtered values, but if filtered values are empty
   * it will fallback to all non constrained values.
   */
  protected evaluateSuggestedValues(): void {
    productWizardConsole.groupCollapsed('evaluateSuggestedValues()');
    productWizardConsole.log('Use filters:', this.useFilters);
    this.attributesSuggested = {};
    for (const attributeName in this.attributes) {
      productWizardConsole.groupCollapsed('evaluateSuggestedValues( ' + attributeName + ' )');
      let values = this.useFilters ? this.getFilteredValues(attributeName) : [];
      productWizardConsole.log(values);
      if (!values || !values.length) {
        values = this.getAllValues(attributeName);
      }
      if (this.hasPreferredValue(attributeName)) {
        let preferredValue = this.getPreferredValue(attributeName);
        productWizardConsole.log('Prefered value:', preferredValue);
        if (preferredValue != null && preferredValue != undefined) {
          if (!Array.isArray(preferredValue)) {
            // eslint-disable-next-line  @typescript-eslint/no-explicit-any
            preferredValue = [preferredValue] as Array<any>;
          }
          // eslint-disable-next-line  @typescript-eslint/no-explicit-any
          preferredValue = (preferredValue as Array<any>).filter(
            (value: string | number | boolean) => {
              productWizardConsole.log(
                'Preferred value is constrained:',
                this.isConstrained(attributeName, value) && this.canValueBe(attributeName, value),
                this.attributes[attributeName].valuesAndConstraints,
              );
              return (
                !this.isConstrained(attributeName, value) &&
                this.canValueBe(attributeName, value) &&
                values.indexOf(value) < 0
              );
            },
          );
          productWizardConsole.log('Will concat preferred values', preferredValue);
          values = values.concat(preferredValue); // Preferred value last
          //				values = preferredValue.concat( values ); // Preferred value first
        }
      }
      this.attributesSuggested[attributeName] = values;
      productWizardConsole.log('Suggested', values);
      productWizardConsole.groupEnd();
    }
    productWizardConsole.groupEnd();
  }

  /**
   * We want to read in the out-of-stock values for "stockable" attributes
   * so that we can still show them but make them unselectable
   * and indicate to the customer that they are not currently in stock.
   */
  protected evaluateOutOfStockValues(): void {
    this.attributesOutOfStock = {};
    for (const attributeName in this.attributes) {
      const values: Array<string> = this.attributes[attributeName].outOfStockValues ?? [];
      if (values.length > 0) {
        this.attributesOutOfStock[attributeName] = values;
      }
    }
  }

  /**
   * WARNING! This needs to be on pair with ProductAttrValueHelper.php
   * Out of stock should be handled outside of this function, so we've consciously excluded it.
   */
  public isAvailable(attributeName: string, value: string | number | boolean): boolean {
    return (
      !this.isConstrained(attributeName, value) &&
      (this.isInSuggestedValues(attributeName, value) || this.isDynamicValue(attributeName))
    );
  }

  /**
   * WARNING! This needs to be on pair with ProductAttrValueHelper.php
   */
  public isUnavailable(attributeName: string, value: string | number | boolean): boolean {
    return !this.isAvailable(attributeName, value);
  }

  public getDefaultValue(attrName: string): AttributeType | Array<unknown> | null {
    if (this.isMultiValue(attrName)) {
      return [];
    }

    const suggestedValues = this.getSuggestedValues(attrName);
    if (suggestedValues && suggestedValues.length) {
      for (const index in suggestedValues) {
        const value = suggestedValues[index];
        if (!this.isConstrained(attrName, value)) {
          return value;
        }
      }
    }

    return null;
  }

  public getAllValues(attributeName: string): Array<string | number | boolean> {
    if (Object.prototype.hasOwnProperty.call(this.attributes, attributeName)) {
      // Object.keys doesn't work because it's turning booleans to strings
      const allValues = [];
      for (const [key] of Object.entries(this.attributes[attributeName].valuesAndConstraints)) {
        let valueToPush: string | number | boolean = key;

        if (this.isBooleanType(attributeName)) {
          try {
            valueToPush = JSON.parse(key) as boolean; // JSON.parse method turns "false" / "true" to boolean
          } catch (e) {
            productWizardConsole.error(e);
          }
        } else if (this.isIntType(attributeName)) {
          valueToPush = parseInt(key);
        } else if (this.isFloatType(attributeName)) {
          valueToPush = parseFloat(key);
        }

        allValues.push(valueToPush);
      }
      return allValues;
    }

    return [];
  }

  /**
   * Iterates through suggested values and return the highest number that isn't constrained.
   *
   * WARNING! This needs to be on pair with ProductAttrValueHelper.php
   *
   * @param attributeName
   */
  public getHighestAvailableValue(attributeName: string): number | null {
    let highestValue: number | null = null;
    const values = this.getSuggestedValues(attributeName);
    values.forEach((value) => {
      if (!this.isConstrained(attributeName, value)) {
        const number = parseFloat(String(value));
        if (!highestValue || number > highestValue) {
          highestValue = number;
        }
      }
    });

    return highestValue;
  }

  /**
   * Iterates through suggested values and return the lowest number that isn't constrained.
   *
   * WARNING! This needs to be on pair with ProductAttrValueHelper.php
   *
   * @param attributeName
   */
  public getLowestAvailableValue(attributeName: string): number | null {
    let lowestValue: number | null = null;
    const values = this.getSuggestedValues(attributeName);
    values.forEach((value) => {
      if (!this.isConstrained(attributeName, value)) {
        const number = parseFloat(String(value));
        if (!lowestValue || number < lowestValue) {
          lowestValue = number;
        }
      }
    });

    return lowestValue;
  }

  /**
   * OBS! Can contain constrained values.
   *
   * WARNING! This needs to be on pair with ProductAttrValueHelper.php
   *
   * @param attributeName
   */
  public getSuggestedValues<T = AttributeType>(attributeName: string): Array<T> {
    if (Object.prototype.hasOwnProperty.call(this.attributesSuggested, attributeName)) {
      return this.attributesSuggested[attributeName] as T[];
    }

    return [];
  }

  public getFilteredValues(attributeName: string): Array<string | number | boolean> {
    if (Object.prototype.hasOwnProperty.call(this.attributesFiltered, attributeName)) {
      return this.attributesFiltered[attributeName];
    }

    return [];
  }

  public hasBeenFiltered(attributeName: string): boolean {
    if (Object.prototype.hasOwnProperty.call(this.attributesFiltersMatched, attributeName)) {
      return this.attributesFiltersMatched[attributeName] > 0;
    }

    return false;
  }

  public hasPreferredValue(attributeName: string) {
    return Object.prototype.hasOwnProperty.call(this.attributesPreferred, attributeName);
  }

  public removePreferredValue(attributeName: string, attributeValue: string | number): void {
    if (this.isMultiValue(attributeName) && this.attributesPreferred[attributeName]) {
      // eslint-disable-next-line  @typescript-eslint/no-explicit-any
      const preferred = this.attributesPreferred[attributeName] as Array<any>;
      this.attributesPreferred[attributeName] = preferred.filter(
        (value) => value != attributeValue,
      );
    } else {
      this.attributesPreferred[attributeName] = null;
    }
  }

  public getPreferredValue(
    attributeName: string,
  ): string | number | string[] | number[] | null | boolean | boolean[] {
    if (this.hasPreferredValue(attributeName)) {
      return this.attributesPreferred[attributeName];
    }

    return null;
  }

  /** Sets a preferred value. Will be available in getSuggestedValues() if not constrained. */

  public setPreferredValue(attributeName: string, attributeValue: string): void {
    this.attributesPreferred[attributeName] = attributeValue;
  }

  public getConstrainedValues(attributeName: string): Array<string | number | boolean> {
    if (Object.prototype.hasOwnProperty.call(this.attributesConstrained, attributeName)) {
      return this.attributesConstrained[attributeName];
    }

    return [];
  }

  public getOutOfStockValues(attributeName: string): Array<string | number | boolean> {
    if (Object.prototype.hasOwnProperty.call(this.attributesOutOfStock, attributeName)) {
      return this.attributesOutOfStock[attributeName];
    }

    return [];
  }

  public getIcons(attributeName: string): Record<string, string> {
    return this.attributes[attributeName]?.icons ?? [];
  }

  public getAttributeNames(): string[] {
    if (this.attributes) {
      return Object.keys(this.attributes);
    }

    return [];
  }

  public isSupported(attrName: string): boolean {
    return Object.prototype.hasOwnProperty.call(this.attributes, attrName);
  }

  public isRequired(attrName: string): boolean {
    return this.isSupported(attrName) && this.attributes[attrName]?.isRequired;
  }

  public isMultiValue(attributeName: string): boolean {
    return this.attributes[attributeName]?.isMultiValue;
  }

  public isDynamicValue(attributeName: string): boolean {
    return this.attributes[attributeName]?.isDynamicValue;
  }

  public isBooleanType(attributeName: string): boolean {
    return this.attributes[attributeName]?.valueType == 'boolean';
  }

  public isIntType(attributeName: string): boolean {
    return this.attributes[attributeName]?.valueType == 'integer';
  }

  public isFloatType(attributeName: string): boolean {
    return this.attributes[attributeName]?.valueType == 'float';
  }

  public isNumberType(attributeName: string): boolean {
    return this.isIntType(attributeName) || this.isFloatType(attributeName);
  }

  public isInFilteredValues(
    attributeName: string,
    attributeValue: string | number | boolean,
  ): boolean {
    const filteredValues = this.getFilteredValues(attributeName);
    if (filteredValues) {
      return filteredValues.indexOf(attributeValue) >= 0;
    }

    return false;
  }

  public isInSuggestedValues(
    attributeName: string,
    attributeValue: string | number | boolean,
  ): boolean {
    const availableValues = this.getSuggestedValues(attributeName);
    if (availableValues) {
      return availableValues.indexOf(attributeValue) >= 0;
    }

    return false;
  }

  public canValueBe(attributeName: string, attributeValue: string | number | boolean): boolean {
    if (!this.isDynamicValue(attributeName)) {
      if (typeof attributeValue == 'boolean') {
        attributeValue = JSON.stringify(attributeValue);
      }
      return Object.prototype.hasOwnProperty.call(
        this.attributes[attributeName]?.valuesAndConstraints,
        attributeValue,
      );
    }

    return true;
  }

  public isConstrained(attributeName: string, attributeValue: string | number | boolean): boolean {
    const disabledValues = this.getConstrainedValues(attributeName);
    if (disabledValues) {
      return (
        disabledValues.indexOf(attributeValue) >= 0 ||
        disabledValues.indexOf(String(attributeValue)) >= 0
      );
    }

    return false;
  }

  public isOutOfStockValues(
    attributeName: string,
    attributeValue: string | number | boolean,
  ): boolean {
    const outOfStockValues = this.getOutOfStockValues(attributeName);
    if (outOfStockValues) {
      return outOfStockValues.indexOf(attributeValue) >= 0;
    }

    return false;
  }

  /* SET AND GET */
  public get debugList(): string[] {
    return this.attrEvaluator.debugList;
  }

  public set debugEnabled(value: boolean) {
    this.attrEvaluator.debugEnabled = value;
  }

  public get debugEnabled(): boolean {
    return this.attrEvaluator.debugEnabled;
  }
}
